<template>
  <v-container
    grid-list-xl
    class="pa-0"
  >
    <v-layout
      align-center
      wrap
      class="px-4"
    >
      <v-flex
        xs6
        sm4
        md2
      >
        <v-select
          v-model="selectedRegion"
          :items="regions"
          item-text="name"
          item-value="id"
          clearable
          label="Region"
          prepend-icon="fa-globe"
        />
      </v-flex>
      <v-flex
        xs6
        sm4
        md2
      >
        <v-select
          v-model="selectedCity"
          :items="cities"
          item-text="name"
          item-value="id"
          clearable
          label="City"
          prepend-icon="fa-map-o"
        />
      </v-flex>
      <v-flex
        xs6
        sm4
        md2
      >
        <v-select
          v-model="selectedBranch"
          :items="branches"
          item-text="name"
          item-value="id"
          clearable
          label="Branch"
          prepend-icon="fa-building"
        />
      </v-flex>
      <v-flex
        xs6
        sm4
        md2
      >
        <v-select
          v-model="selectedDepartment"
          :items="departments"
          item-text="name"
          item-value="id"
          clearable
          label="Department"
          prepend-icon="fa-bullseye"
        />
      </v-flex>
      <v-flex
        xs6
        sm4
        md2
      >
        <v-menu
          v-model="showDatePicker"
          :close-on-content-click="false"
          :nudge-right="40"
          full-width
          min-width="290px"
          offset-y
          transition="scale-transition"
        >
          <template v-slot:activator="{ on }">
            <v-text-field
              v-model="computedDateFormatted"
              label="Week Starting"
              prepend-icon="fa-calendar-o"
              readonly
              v-on="on"
            />
          </template>
          <v-date-picker
            v-model="selectedDate"
            :allowed-dates="allowedDates"
            first-day-of-week="1"
            scrollable
            show-week
            @input="showDatePicker = false"
          />
        </v-menu>
      </v-flex>
      <v-flex
        xs6
        sm4
        md2
      >
        <v-btn
          block
          color="accent"
          @click="loadCandidates()"
        >
          <v-icon left>
            fa-refresh
          </v-icon>
          Get Workers
        </v-btn>
      </v-flex>
    </v-layout>

    <v-layout
      align-center
      wrap
    >
      <v-flex
        xs12
        class="py-0"
      >
        <v-data-table
          v-model="selectedCandidates"
          :headers="headers"
          :items="candidates"
          :items-per-page="15"
          :loading="loading"
          :search="search"
          :sort-by="['isRostered', 'firstName', 'lastName']"
          :sort-desc="[true, false, false]"
          class="elevation-1"
          multi-sort
          show-select
        >
          <template v-slot:top>
            <v-toolbar flat>
              <v-btn
                color="secondary"
                class="mr-2"
                @click="newCandidate()"
              >
                <v-icon left>
                  fa-plus
                </v-icon>
                New Worker
              </v-btn>
              <v-btn
                :disabled="!selectedCandidates.length"
                class="mx-2"
                color="primary lighten-2"
                @click="documentDownloads.dialog = true"
              >
                <v-icon left>
                  fa-download
                </v-icon>
                Documents
              </v-btn>
              <v-spacer />
              <v-text-field
                v-model="search"
                append-icon="fa-search"
                label="Search"
                hide-details
                single-line
              />
            </v-toolbar>
          </template>

          <template v-slot:item.id="{ item }">
            <v-avatar>
              <img
                :src="'/api/candidate/image/' + item.id + '?pixel=48'"
                alt="Avatar"
              >
            </v-avatar>
          </template>

          <template v-slot:item.isRostered="{ item }">
            {{ item.isRostered ? 'Rostered' : '' }}
          </template>

          <template v-slot:item.action="{ item }">
            <v-btn
              color="primary lighten-2"
              text
              small
              @click="editCandidate(item)"
            >
              <v-icon left>
                fa-edit
              </v-icon>
              edit
            </v-btn>
            <v-btn
              color="warning"
              text
              small
              @click.stop="(deleting.dialog = true) && (deleting.candidate = item)"
            >
              <v-icon left>
                fa-remove
              </v-icon>
              delete
            </v-btn>
          </template>
        </v-data-table>
      </v-flex>
    </v-layout>

    <v-dialog
      v-model="deleting.dialog"
      max-width="600px"
    >
      <v-card v-if="deleting.candidate">
        <v-card-title class="headline">
          Deleting {{ deleting.candidate.firstName }} {{ deleting.candidate.lastName }}
        </v-card-title>

        <v-card-text v-if="!deleting.inProgress">
          Are you sure you want to delete this worker?
        </v-card-text>

        <v-card-text v-if="deleting.inProgress">
          Please stand by
          <v-progress-linear
            indeterminate
            color="primary"
          />
        </v-card-text>

        <v-card-actions>
          <v-spacer />

          <v-btn
            :disabled="deleting.inProgress"
            color="warning"
            @click="deleteCandidate(deleting.candidate)"
          >
            Yes
          </v-btn>

          <v-btn
            :disabled="deleting.inProgress"
            @click="deleting.dialog = false"
          >
            No
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog
      v-model="documentDownloads.dialog"
      max-width="600px"
      scrollable
    >
      <v-card>
        <v-card-title>Document Types</v-card-title>
        <v-divider class="ma-0" />
        <v-card-text style="height: 300px;">
          <v-checkbox
            v-for="complianceType in complianceTypes"
            v-show="!documentDownloads.inProgress"
            :key="complianceType.id"
            v-model="documentDownloads.selectedComplianceTypes"
            class="mt-0"
            color="primary"
            :label="complianceType.name"
            :value="complianceType.id"
          />

          <v-layout
            v-if="documentDownloads.inProgress"
            align-content-center
            justify-center
            fill-height
            wrap
          >
            <v-flex
              xs12
              subtitle-1
              text-center
            >
              Getting files
            </v-flex>
            <v-flex xs6>
              <v-progress-linear
                indeterminate
                rounded
                height="6"
              />
            </v-flex>
          </v-layout>
        </v-card-text>
        <v-divider class="ma-0" />
        <v-card-actions>
          <v-btn
            :disabled="documentDownloads.inProgress"
            @click="selectAllComplianceTypes()"
          >
            <v-icon left>
              fa-check-square-o
            </v-icon>
            Select All
          </v-btn>
          <v-spacer />
          <v-btn
            :disabled="
              !documentDownloads.selectedComplianceTypes.length
                || documentDownloads.inProgress
            "
            color="accent"
            @click="downloadDocuments()"
          >
            <v-icon left>
              fa-download
            </v-icon>
            Download
          </v-btn>
          <v-btn
            @click="documentDownloads.dialog = false"
          >
            Close
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar
      v-model="snackbar.show"
      bottom
      color="error"
      multi-line
    >
      {{ snackbar.text }}
      <v-btn
        dark
        text
        @click="snackbar.show = false"
      >
        Close
      </v-btn>
    </v-snackbar>
  </v-container>
</template>

<script>
import { DateTime } from 'luxon';
import axios from 'axios';
import qs from 'qs';

export default {
  components: {
  },

  data() {
    return {
      snackbar: {
        show: false,
        text: '',
      },
      showDatePicker: false,

      loading: false,
      deleting: {
        candidate: null,
        dialog: false,
        inProgress: false,
      },
      documentDownloads: {
        selectedComplianceTypes: [],
        dialog: false,
        inProgress: false,
      },

      // TODO: This should all be in the Vuex store.
      complianceTypes: window.complianceItems,
      regions: window.regions,
      cities: window.cities,
      branches: window.branches,
      departments: window.departments,
      candidates: [],

      selectedDate: DateTime.local().startOf('week').toISODate(),
      selectedRegion: null,
      selectedCity: null,
      selectedBranch: null,
      selectedDepartment: null,
      selectedCandidates: [],

      search: '',
      headers: [
        {
          align: 'left',
          sortable: false,
          text: 'Photo',
          value: 'id',
        },
        { text: 'Rating', value: 'companyRating' },
        { text: 'First Name', value: 'firstName' },
        { text: 'Last Name', value: 'lastName' },
        { text: 'Rostered This Week', value: 'isRostered' },
        {
          align: 'right',
          sortable: false,
          text: 'Actions',
          value: 'action',
        },
      ],
    };
  },

  computed: {
    computedDateFormatted() {
      if (!this.selectedDate) return null;
      const [year, month, day] = this.selectedDate.split('-');
      return `${day}/${month}/${year}`;
    },
  },

  mounted() {
    this.loadCandidates();
  },

  methods: {
    allowedDates: val => (new Date(val)).getDay() === 1,

    async loadCandidates() {
      this.loading = true;
      try {
        const response = await axios({
          method: 'GET',
          url: '/api/candidates/search/',
          params: {
            branchId: this.selectedBranch,
            deptId: this.selectedDepartment,
          },
        });
        this.candidates = response.data.data;
      } catch (error) {
        // TODO: Need to touch/copy this code? Make the snackbar to be a component.
        if (error.response) {
          this.snackbar.text = `Error: ${error.response.status} in ${error.request.responseURL}. Please try refreshing the page, or contact us if the problem persists.`;
        } else {
          this.snackbar.text = 'An unknown error occurred. Please try refreshing the page, or contact us if the problem persists.';
        }
        this.snackbar.show = true;
      } finally {
        this.loading = false;
      }
    },

    newCandidate() {
      window.candidateModalApp.showModal(0, this.loadCandidates);
    },

    editCandidate(candidate) {
      window.candidateModalApp.showModal(candidate.id, this.loadCandidates);
    },

    async deleteCandidate(candidate) {
      this.deleting.inProgress = true;
      try {
        await axios({
          method: 'DELETE',
          url: `/api/candidates/${candidate.id}`,
          params: {},
        });
      } catch (error) {
        // TODO: Need to touch/copy this code? Make the snackbar to be a component.
        if (error.response) {
          this.snackbar.text = `Error: ${error.response.status} in ${error.request.responseURL}. Please try refreshing the page, or contact us if the problem persists.`;
        } else {
          this.snackbar.text = 'An unknown error occurred. Please try refreshing the page, or contact us if the problem persists.';
        }
        this.snackbar.show = true;
      } finally {
        this.deleting.dialog = false;
        this.deleting.inProgress = false;
        this.loadCandidates();
      }
    },

    selectAllComplianceTypes() {
      if (this.complianceTypes.length === this.documentDownloads.selectedComplianceTypes.length) {
        this.documentDownloads.selectedComplianceTypes = [];
      } else {
        this.documentDownloads.selectedComplianceTypes = this.complianceTypes.map(e => e.id);
      }
    },

    async downloadDocuments() {
      this.documentDownloads.inProgress = true;
      try {
        const response = await axios({
          method: 'GET',
          url: '/api/documents/download/',
          params: {
            candidateIds: this.selectedCandidates.map(e => e.id),
            complianceItemTypeIds: this.documentDownloads.selectedComplianceTypes,
          },
          // This serializer produces candidateIds[0]=1&candidateIds[1]=2... Which .NET understands.
          // Default candidateIds[]=1&candidateIds[]=2...
          paramsSerializer: params => qs.stringify(params),
          responseType: 'blob',
        });
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'documents.zip');
        document.body.appendChild(link);
        link.click();
      } catch (error) {
        // TODO: Need to touch/copy this code? Make the snackbar to be a component.
        if (error.response) {
          this.snackbar.text = `Error: ${error.response.status} in ${error.request.responseURL}. Please try refreshing the page, or contact us if the problem persists.`;
        } else {
          this.snackbar.text = 'An unknown error occurred. Please try refreshing the page, or contact us if the problem persists.';
        }
        this.snackbar.show = true;
      } finally {
        this.documentDownloads.inProgress = false;
      }
    },
  },
};
</script>
